export const findErrorInSharechatLink = link => {
	// add https in the end if necessary
	const regex = /^https:\/\/sharechat\.com\/(post|video|item)\/[a-zA-Z0-9_-]+(\?.*)?$/;
	let newHref = link;
	const hasHttpOrHttps = link.match(/^https?:\/\//);
	if (!hasHttpOrHttps) {
		newHref = `https://${link}`;
	}
	try {
		const url = new URL(newHref);
		const isValid = regex.test(url.href);
		if (!url.hostname.endsWith("sharechat.com")) {
			return `${link} is not sharechat link`;
		} else if (!isValid) {
			return `${link} Please submit link(s) to specific posts only. Profile links are not supported.`;
		}
	} catch (error) {
		return `${link} is not a valid url`;
	}
	return null;
};

// matches any letter in any script (latin, devanagari, tamil, etc.)
const hasLetter = value => /\p{L}/u.test(value);

// for fields that are names of things: letters required, digits not allowed
const nameLikeValidator = (label, maxLength) => value => {
	if (value.length > maxLength) {
		return `${label} is too long (max ${maxLength} characters)`;
	}
	if (/\p{N}/u.test(value) || !hasLetter(value)) {
		return `Please enter a valid ${label.toLowerCase()}`;
	}
	return null;
};

// for free-text fields that may contain digits but must contain some letters
const textValidator = (label, maxLength) => value => {
	if (value.length > maxLength) {
		return `${label} is too long (max ${maxLength} characters)`;
	}
	if (!hasLetter(value)) {
		return `Please enter a valid ${label.toLowerCase()}`;
	}
	return null;
};

// markup is never legitimate in these inputs — same rule as the Moj series-title field.
// Frontend rejection is UX/defense-in-depth; render-time escaping stays the real XSS defense.
const UNSAFE_CHARS_REGEX = /[<>]/;
const UNSAFE_CHARS_ERROR = "Characters < and > are not allowed";

const rejectUnsafeThen = validator => value => {
	if (UNSAFE_CHARS_REGEX.test(value)) {
		return UNSAFE_CHARS_ERROR;
	}
	return validator ? validator(value) : null;
};

// for optional free-text fields: only guard against markup and absurd length
const safeTextValidator = maxLength => value => {
	if (value.length > maxLength) {
		return `Too long (max ${maxLength} characters)`;
	}
	return null;
};

// Each validator takes a trimmed non-empty string and returns an error message or null.
// Keys match the corresponding data property on the copyright claim form.
export const claimFieldValidators = {
	email: rejectUnsafeThen(value => {
		if (value.length > 254 || !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value)) {
			return "Please enter a valid email address";
		}
		return null;
	}),
	phone: rejectUnsafeThen(value => {
		if (!/^\+?[\d\s()-]{7,16}$/.test(value) || (value.match(/\d/g) || []).length < 7) {
			return "Please enter a valid phone number";
		}
		return null;
	}),
	pin: rejectUnsafeThen(value => {
		if (!/^[a-zA-Z\d][a-zA-Z\d\s-]{2,9}$/.test(value) || !/\d/.test(value)) {
			return "Please enter a valid pincode";
		}
		return null;
	}),
	personalName: rejectUnsafeThen(nameLikeValidator("Name", 100)),
	city: rejectUnsafeThen(nameLikeValidator("City", 100)),
	country: rejectUnsafeThen(nameLikeValidator("Country", 60)),
	orgName: rejectUnsafeThen(textValidator("Name / organisation name", 200)),
	address: rejectUnsafeThen(textValidator("Address", 500)),
	// optional / conditional free-text fields — markup and length guard only
	designation: rejectUnsafeThen(safeTextValidator(100)),
	onBehalf: rejectUnsafeThen(safeTextValidator(150)),
	landmark: rejectUnsafeThen(safeTextValidator(150)),
	otherClaimType: rejectUnsafeThen(safeTextValidator(500)),
	otherAffectedPerson: rejectUnsafeThen(safeTextValidator(500)),
	otherUnauthorisedUseReason: rejectUnsafeThen(safeTextValidator(500)),
	otherAbilityHamperReason: rejectUnsafeThen(safeTextValidator(500)),
	otherRelationWithOwner: rejectUnsafeThen(safeTextValidator(500)),
	otherInfringementReason: rejectUnsafeThen(safeTextValidator(500))
};
